import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { mailConfigSave, mailConfigGet, mailConfigTest } from "../../redux/actions/action-creator";
import CreateConfirm from "../../components/Modal/CreateConfirm";
import "react-toastify/dist/ReactToastify.css";
import "../../components/home_page/section.css";
import "../../components/home_page/footer.css";
import "../../components/login_form/registration.css";
import "../gatewayoftally/transaction/voucher.css";

// SMTP presets
const SMTP_PRESETS = {
  gmail:   { label: "Gmail",           mail_host: "smtp.gmail.com",        mail_port: 465, mail_encryption: "ssl" },
  outlook: { label: "Outlook/Hotmail", mail_host: "smtp-mail.outlook.com", mail_port: 587, mail_encryption: "tls" },
  yahoo:   { label: "Yahoo",           mail_host: "smtp.mail.yahoo.com",    mail_port: 587, mail_encryption: "tls" },
  custom:  { label: "Custom SMTP",     mail_host: "",                       mail_port: 587, mail_encryption: "tls" },
};

const FIELDS_ORDER = [
  "mail_from_name",
  "mail_from_email",
  "show_additional_options",
  "mail_username",
  "mail_password",
  "mail_server_preset",
  "mail_host",
  "mail_port",
  "use_ssl",
  "use_ssl_standard_port",
  "test_to",
];

function MailConfiguration() {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const { userData } = useSelector((s) => s.auth);
  const company_id = userData?.company_id || localStorage.getItem("company_id");

  const [formData, setFormData] = useState({
    mail_host: "smtp.gmail.com",
    mail_port: 465,
    mail_encryption: "ssl",
    mail_username: "",
    mail_password: "",
    mail_from_name: "",
    mail_from_email: "",
    test_to: "",
  });

  const [showAdditionalOptions, setShowAdditionalOptions] = useState("Yes");
  const [useSsl, setUseSsl] = useState("Yes");
  const [useSslStandard, setUseSslStandard] = useState("No");

  const [isVerified,    setIsVerified]    = useState(false);
  const [isSaving,      setIsSaving]      = useState(false);
  const [isTesting,     setIsTesting]     = useState(false);
  const [hasExisting,   setHasExisting]   = useState(false);
  const [showPassword,  setShowPassword]  = useState(false);
  const [activePreset,  setActivePreset]  = useState("gmail");

  const [showQuitModal, setShowQuitModal]     = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showTestModal, setShowTestModal]     = useState(false);

  const lastFocusedElement = useRef(null);

  // Refs for keyboard navigation
  const ref_mail_from_name        = useRef(null);
  const ref_mail_from_email       = useRef(null);
  const ref_show_additional_options = useRef(null);
  const ref_mail_username         = useRef(null);
  const ref_mail_password         = useRef(null);
  const ref_mail_server_preset    = useRef(null);
  const ref_mail_host             = useRef(null);
  const ref_mail_port             = useRef(null);
  const ref_use_ssl               = useRef(null);
  const ref_use_ssl_standard_port = useRef(null);
  const ref_test_to               = useRef(null);

  const refs = {
    mail_from_name:          ref_mail_from_name,
    mail_from_email:         ref_mail_from_email,
    show_additional_options: ref_show_additional_options,
    mail_username:           ref_mail_username,
    mail_password:           ref_mail_password,
    mail_server_preset:      ref_mail_server_preset,
    mail_host:               ref_mail_host,
    mail_port:               ref_mail_port,
    use_ssl:                 ref_use_ssl,
    use_ssl_standard_port:   ref_use_ssl_standard_port,
    test_to:                 ref_test_to,
  };

  // Load existing config on mount
  useEffect(() => {
    if (!company_id) return;
    const fd = new FormData();
    fd.append("company_id", company_id);
    dispatch(mailConfigGet(fd))
      .then((res) => {
        if (res?.status && res?.data) {
          const loadedData = {
            mail_host:       res.data.mail_host       || "smtp.gmail.com",
            mail_port:       parseInt(res.data.mail_port) || 465,
            mail_encryption: res.data.mail_encryption || "ssl",
            mail_username:   res.data.mail_username   || "",
            mail_password:   res.data.mail_password   || "",
            mail_from_name:  res.data.mail_from_name  || "",
            mail_from_email: res.data.mail_from_email || "",
            test_to:         res.data.mail_username   || "",
          };
          setFormData(loadedData);
          setIsVerified(Number(res.data.is_verified) === 1);
          setHasExisting(true);

          // Map encryption to SSL dropdowns
          const enc = loadedData.mail_encryption;
          if (enc === "ssl") {
            setUseSsl("Yes");
            setUseSslStandard("No");
          } else if (enc === "tls") {
            setUseSsl("No");
            setUseSslStandard("Yes");
          } else {
            setUseSsl("No");
            setUseSslStandard("No");
          }
        }
      })
      .catch(() => {});
  }, [dispatch, company_id]);

  // Focus first input on load
  useEffect(() => {
    setTimeout(() => {
      ref_mail_from_name.current?.focus();
    }, 50);
  }, []);

  // Auto-detect preset from host
  useEffect(() => {
    const h = formData.mail_host || "";
    if      (h.includes("gmail"))                             setActivePreset("gmail");
    else if (h.includes("outlook") || h.includes("hotmail")) setActivePreset("outlook");
    else if (h.includes("yahoo"))                            setActivePreset("yahoo");
    else if (h)                                              setActivePreset("custom");
  }, [formData.mail_host]);

  // Keyboard: Escape and Ctrl+A / Ctrl+T hotkeys
  useEffect(() => {
    const handler = (e) => {
      if (showQuitModal || showAcceptModal || showTestModal) return;

      if (e.key === "Escape") {
        e.preventDefault();
        lastFocusedElement.current = document.activeElement;
        setShowQuitModal(true);
      }

      if (e.ctrlKey && e.key.toLowerCase() === "a") {
        e.preventDefault();
        lastFocusedElement.current = document.activeElement;
        setShowAcceptModal(true);
      }

      if ((e.ctrlKey || e.altKey) && e.key.toLowerCase() === "t") {
        e.preventDefault();
        lastFocusedElement.current = document.activeElement;
        setShowTestModal(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showQuitModal, showAcceptModal, showTestModal]);

  // Enter-key and Shift+Enter field navigation
  const handleKeyDown = (e, fieldName) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const idx = FIELDS_ORDER.indexOf(fieldName);
      if (e.shiftKey) {
        // Go backwards
        let prevIdx = idx - 1;
        while (prevIdx >= 0) {
          const prevFieldName = FIELDS_ORDER[prevIdx];
          const isHidden = showAdditionalOptions === "No" && [
            "mail_username", "mail_password", "mail_server_preset",
            "mail_host", "mail_port", "use_ssl", "use_ssl_standard_port"
          ].includes(prevFieldName);

          if (!isHidden && refs[prevFieldName]?.current) {
            refs[prevFieldName].current.focus();
            return;
          }
          prevIdx--;
        }
      } else {
        // Go forwards
        let nextIdx = idx + 1;
        while (nextIdx < FIELDS_ORDER.length) {
          const nextFieldName = FIELDS_ORDER[nextIdx];
          const isHidden = showAdditionalOptions === "No" && [
            "mail_username", "mail_password", "mail_server_preset",
            "mail_host", "mail_port", "use_ssl", "use_ssl_standard_port"
          ].includes(nextFieldName);

          if (!isHidden && refs[nextFieldName]?.current) {
            refs[nextFieldName].current.focus();
            return;
          }
          nextIdx++;
        }
        // Last field, trigger accept modal
        lastFocusedElement.current = e.target;
        setShowAcceptModal(true);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (isVerified) setIsVerified(false);
  };

  // E-mail server dropdown handler
  const handleServerChange = (e) => {
    const val = e.target.value;
    setActivePreset(val);

    const preset = SMTP_PRESETS[val];
    if (preset) {
      setFormData((prev) => ({
        ...prev,
        mail_host: preset.mail_host,
        mail_port: preset.mail_port,
        mail_encryption: preset.mail_encryption,
      }));

      if (preset.mail_encryption === "ssl") {
        setUseSsl("Yes");
        setUseSslStandard("No");
      } else {
        setUseSsl("No");
        setUseSslStandard("Yes");
      }
    }
  };

  // Encryption helper
  const updateEncryption = (ssl, sslStd) => {
    let enc = "none";
    if (ssl === "Yes") {
      enc = "ssl";
    } else if (sslStd === "Yes") {
      enc = "tls";
    }
    setFormData((p) => ({ ...p, mail_encryption: enc }));
  };

  // Use SSL dropdown handler
  const handleUseSslChange = (e) => {
    const val = e.target.value;
    setUseSsl(val);
    const newSslStd = val === "Yes" ? "No" : useSslStandard;
    if (val === "Yes") setUseSslStandard("No");
    updateEncryption(val, newSslStd);

    setFormData((prev) => {
      let port = prev.mail_port;
      if (val === "Yes" && prev.mail_port === 587) port = 465;
      if (val === "No" && prev.mail_port === 465) port = 587;
      return { ...prev, mail_port: port };
    });
  };

  // Use SSL/TLS on standard port dropdown handler
  const handleUseSslStandardChange = (e) => {
    const val = e.target.value;
    setUseSslStandard(val);
    const newSsl = val === "Yes" ? "No" : useSsl;
    if (val === "Yes") setUseSsl("No");
    updateEncryption(newSsl, val);

    setFormData((prev) => {
      let port = prev.mail_port;
      if (val === "Yes" && prev.mail_port === 465) port = 587;
      return { ...prev, mail_port: port };
    });
  };

  // Accept / Save handler
  const handleSave = async () => {
    setShowAcceptModal(false);
    if (!formData.mail_host || !formData.mail_username || !formData.mail_password) {
      toast.error("SMTP Host, Username aur Password required hain!", { position: toast.POSITION.TOP_CENTER, autoClose: 3000 });
      return;
    }
    setIsSaving(true);
    try {
      const fd = new FormData();
      fd.append("company_id", company_id);
      Object.entries(formData).forEach(([k, v]) => {
        if (k !== "test_to") fd.append(k, v);
      });
      const res = await dispatch(mailConfigSave(fd));
      if (res?.status) {
        toast.success("✅ Mail configuration saved!", { position: toast.POSITION.TOP_CENTER, autoClose: 3000 });
        setHasExisting(true);
        setIsVerified(false);
      } else {
        toast.error(res?.message || "Save failed", { position: toast.POSITION.TOP_CENTER, autoClose: 3000 });
      }
    } catch {
      toast.error("Error occurred", { position: toast.POSITION.TOP_CENTER, autoClose: 3000 });
    } finally {
      setIsSaving(false);
    }
  };

  // Test Mail handler
  const handleTest = async () => {
    setShowTestModal(false);
    setIsTesting(true);
    try {
      const fd = new FormData();
      fd.append("company_id", company_id);
      Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
      const res = await dispatch(mailConfigTest(fd));
      if (res?.status) {
        toast.success("✅ " + (res.message || "Test email sent!"), { position: toast.POSITION.TOP_CENTER, autoClose: 5000 });
        setIsVerified(true);
      } else {
        toast.error("❌ " + (res?.message || "Test failed"), { position: toast.POSITION.TOP_CENTER, autoClose: 6000 });
      }
    } catch {
      toast.error("Error occurred", { position: toast.POSITION.TOP_CENTER, autoClose: 4000 });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <>
      <ToastContainer />

      {/* Confirmation Modals */}
      {showQuitModal && (
        <CreateConfirm
          message="Quit"
          handleSubmit={() => {
            setShowQuitModal(false);
            navigate(-1);
          }}
          handleFocusReset={() => {
            setShowQuitModal(false);
            setTimeout(() => lastFocusedElement.current?.focus(), 10);
          }}
        />
      )}

      {showAcceptModal && (
        <CreateConfirm
          message="Accept"
          handleSubmit={handleSave}
          handleFocusReset={() => {
            setShowAcceptModal(false);
            setTimeout(() => lastFocusedElement.current?.focus(), 10);
          }}
        />
      )}

      {showTestModal && (
        <CreateConfirm
          message={`Send Test Email to ${formData.test_to || formData.mail_username}`}
          handleSubmit={handleTest}
          handleFocusReset={() => {
            setShowTestModal(false);
            setTimeout(() => lastFocusedElement.current?.focus(), 10);
          }}
        />
      )}

      {/* Tally Card styling */}
      <style>{`
        .tally-card-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: calc(82vh - 20px);
          background-color: #ffffff;
          padding: 20px;
          box-sizing: border-box;
        }
        .tally-card {
          background-color: #ffffff;
          border: 1px solid #7f7f7f;
          width: 580px;
          padding: 30px 40px;
          box-sizing: border-box;
        }
        .tally-card-title {
          text-align: center;
          font-weight: bold;
          font-size: 15px;
          text-decoration: underline;
          margin-bottom: 25px;
          color: #000000;
        }
        .tally-row {
          display: flex;
          align-items: center;
          margin-bottom: 4px;
          font-size: 13px;
          color: #000000;
        }
        .tally-label {
          width: 220px;
          text-align: left;
          white-space: nowrap;
        }
        .tally-colon {
          width: 15px;
          text-align: center;
        }
        .tally-value-container {
          flex: 1;
          display: flex;
          align-items: center;
          position: relative;
        }
        .tally-input {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          padding: 2px 5px;
          font-size: 13px;
          color: #000;
          box-sizing: border-box;
        }
        .tally-input:focus {
          background-color: #fffae6;
          border: 1px solid #007acc;
          padding: 1px 4px;
        }
        .tally-select {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          padding: 2px 5px;
          font-size: 13px;
          color: #000;
          cursor: pointer;
          box-sizing: border-box;
        }
        .tally-select:focus {
          background-color: #fffae6;
          border: 1px solid #007acc;
          padding: 1px 4px;
        }
        .tally-subtext-row {
          font-size: 11px;
          font-style: italic;
          color: #333333;
          margin-top: -2px;
          margin-bottom: 6px;
          line-height: 1.2;
        }
      `}</style>

      {/* Main Section */}
      <div className="section">
        {/* Left main content (detail) */}
        <div className="detail" style={{ pointerEvents: (showQuitModal || showAcceptModal || showTestModal) ? "none" : "" }}>
          
          {/* Header Bar */}
          <div className="container-fluid p-0">
            <div className="row table_date_header">
              <div className="col-12 d-flex justify-content-between table_header_font">
                <b>
                  E-Mail Configuration
                  {isVerified  && <span style={{ marginLeft:8, fontSize:10, background:'#27ae60', color:'#fff', padding:'0 6px', borderRadius:8 }}>✅ Verified</span>}
                  {hasExisting && !isVerified && <span style={{ marginLeft:8, fontSize:10, background:'#2980b9', color:'#fff', padding:'0 6px', borderRadius:8 }}>💾 Saved</span>}
                </b>
              </div>
            </div>
          </div>

          {/* Form Area */}
          <div className="container-fluid p-0" style={{ overflowY: "auto", flex: 1, backgroundColor: "#ffffff" }}>
            <div className="tally-card-container">
              <div className="tally-card">
                <div className="tally-card-title">Modify E-mail Profile</div>

                {/* E-mail sender name */}
                <div className="tally-row">
                  <label className="tally-label">E-mail sender name</label>
                  <span className="tally-colon">:</span>
                  <div className="tally-value-container">
                    <input
                      ref={ref_mail_from_name}
                      name="mail_from_name"
                      value={formData.mail_from_name}
                      onChange={handleChange}
                      onKeyDown={(e) => handleKeyDown(e, "mail_from_name")}
                      placeholder="e.g. Yadav Krishi Seva Kendra"
                      className="tally-input"
                    />
                  </div>
                </div>

                {/* E-mail from */}
                <div className="tally-row">
                  <label className="tally-label">E-mail from</label>
                  <span className="tally-colon">:</span>
                  <div className="tally-value-container">
                    <input
                      ref={ref_mail_from_email}
                      name="mail_from_email"
                      type="email"
                      value={formData.mail_from_email}
                      onChange={handleChange}
                      onKeyDown={(e) => handleKeyDown(e, "mail_from_email")}
                      placeholder="e.g. hiralalyadav84@gmail.com"
                      className="tally-input"
                    />
                  </div>
                </div>

                {/* Show additional options */}
                <div className="tally-row">
                  <label className="tally-label">Show additional options</label>
                  <span className="tally-colon">:</span>
                  <div className="tally-value-container">
                    <select
                      ref={ref_show_additional_options}
                      name="show_additional_options"
                      value={showAdditionalOptions}
                      onChange={(e) => setShowAdditionalOptions(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, "show_additional_options")}
                      className="tally-select"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                {/* Additional Options */}
                {showAdditionalOptions === "Yes" && (
                  <>
                    {/* Username */}
                    <div className="tally-row">
                      <label className="tally-label">Username(for authentication)</label>
                      <span className="tally-colon">:</span>
                      <div className="tally-value-container">
                        <input
                          ref={ref_mail_username}
                          name="mail_username"
                          type="email"
                          value={formData.mail_username}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "mail_username")}
                          placeholder="hiralalyadav84@gmail.com"
                          className="tally-input"
                        />
                      </div>
                    </div>

                    {/* Password */}
                    <div className="tally-row">
                      <label className="tally-label">App Password</label>
                      <span className="tally-colon">:</span>
                      <div className="tally-value-container">
                        <input
                          ref={ref_mail_password}
                          name="mail_password"
                          type={showPassword ? "text" : "password"}
                          value={formData.mail_password}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "mail_password")}
                          placeholder={hasExisting ? "••••••••" : "App Password"}
                          className="tally-input"
                          style={{ paddingRight: "35px" }}
                        />
                        <span
                          onClick={() => setShowPassword((p) => !p)}
                          style={{
                            position: "absolute",
                            right: "5px",
                            cursor: "pointer",
                            fontSize: "11px",
                            userSelect: "none",
                            color: "#007acc",
                            fontWeight: "bold"
                          }}
                        >
                          {showPassword ? "Hide" : "Show"}
                        </span>
                      </div>
                    </div>

                    {/* E-mail Server */}
                    <div className="tally-row">
                      <label className="tally-label">E-mail Server</label>
                      <span className="tally-colon">:</span>
                      <div className="tally-value-container">
                        <select
                          ref={ref_mail_server_preset}
                          name="mail_server_preset"
                          value={activePreset}
                          onChange={handleServerChange}
                          onKeyDown={(e) => handleKeyDown(e, "mail_server_preset")}
                          className="tally-select"
                        >
                          <option value="gmail">Gmail</option>
                          <option value="outlook">Outlook/Hotmail</option>
                          <option value="yahoo">Yahoo</option>
                          <option value="custom">Custom SMTP</option>
                        </select>
                      </div>
                    </div>

                    {/* Gmail App Password instructions */}
                    {activePreset === "gmail" && (
                      <div style={{ background: "#fffbe6", borderLeft: "3px solid #f39c12", padding: "4px 8px", fontSize: "11px", color: "#5a4000", margin: "4px 0 8px 0" }}>
                        📌 <b>Gmail:</b> Normal password won't work. Generate an{" "}
                        <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" style={{ color: "#1a6dc7", textDecoration: "underline" }}>
                          App Password
                        </a>.
                      </div>
                    )}

                    {/* Server address */}
                    <div className="tally-row">
                      <label className="tally-label">Server address</label>
                      <span className="tally-colon">:</span>
                      <div className="tally-value-container">
                        <input
                          ref={ref_mail_host}
                          name="mail_host"
                          value={formData.mail_host}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "mail_host")}
                          placeholder="smtp.gmail.com"
                          className="tally-input"
                        />
                      </div>
                    </div>

                    {/* Port Number */}
                    <div className="tally-row">
                      <label className="tally-label">Port Number</label>
                      <span className="tally-colon">:</span>
                      <div className="tally-value-container">
                        <input
                          ref={ref_mail_port}
                          name="mail_port"
                          type="number"
                          value={formData.mail_port}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "mail_port")}
                          className="tally-input"
                        />
                      </div>
                    </div>

                    {/* Use SSL */}
                    <div className="tally-row">
                      <label className="tally-label">Use SSL</label>
                      <span className="tally-colon">:</span>
                      <div className="tally-value-container">
                        <select
                          ref={ref_use_ssl}
                          name="use_ssl"
                          value={useSsl}
                          onChange={handleUseSslChange}
                          onKeyDown={(e) => handleKeyDown(e, "use_ssl")}
                          className="tally-select"
                        >
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                    </div>
                    <div className="tally-subtext-row">
                      (Select 'Yes' if you are using Secured SMTP server)
                    </div>

                    {/* Use SSL/TLS on standard port */}
                    <div className="tally-row">
                      <label className="tally-label">Use SSL/TLS on standard port</label>
                      <span className="tally-colon">:</span>
                      <div className="tally-value-container">
                        <select
                          ref={ref_use_ssl_standard_port}
                          name="use_ssl_standard_port"
                          value={useSslStandard}
                          onChange={handleUseSslStandardChange}
                          onKeyDown={(e) => handleKeyDown(e, "use_ssl_standard_port")}
                          className="tally-select"
                        >
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                    </div>
                    <div className="tally-subtext-row">
                      (Select 'Yes' if you are using SSL on default/Standard SMTP port)
                    </div>
                  </>
                )}

                {/* Send Test Mail recipient */}
                <div className="tally-row" style={{ marginTop: "15px", borderTop: "1px dashed #ccc", paddingTop: "10px" }}>
                  <label className="tally-label">Send Test Mail To</label>
                  <span className="tally-colon">:</span>
                  <div className="tally-value-container">
                    <input
                      ref={ref_test_to}
                      name="test_to"
                      type="email"
                      value={formData.test_to}
                      onChange={handleChange}
                      onKeyDown={(e) => handleKeyDown(e, "test_to")}
                      placeholder="recipient@example.com"
                      className="tally-input"
                    />
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Footer Bar */}
          <div className="footer">
            <div className="boxfooter">
              <button className="button" onClick={() => setShowQuitModal(true)}>
                <strong>Esc: Back</strong>
              </button>
            </div>
            <div className="boxfooter"><button className="button" disabled /></div>
            <div className="boxfooter"><button className="button" disabled /></div>
            <div className="boxfooter">
              <button className="button" onClick={() => setShowAcceptModal(true)} disabled={isSaving}>
                <strong><u>A</u>: Accept</strong>
              </button>
            </div>
            <div className="boxfooter">
              <button className="button" onClick={() => setShowTestModal(true)} disabled={isTesting}>
                <strong><u>T</u>: Test Mail</strong>
              </button>
            </div>
            <div className="boxfooter"><button className="button" disabled /></div>
            <div className="boxfooter"><button className="button" disabled /></div>
            <div className="boxfooter"><button className="button" disabled /></div>
            <div className="boxfooter"><button className="button" disabled /></div>
            <div className="boxfooter"><button className="button" disabled /></div>
          </div>

        </div>

        {/* Right panel (blank) */}
        <div className="help border_1_1px_left backgroud_color_1 position-relative"></div>
      </div>
    </>
  );
}

export default MailConfiguration;
